'use client';

import type { KeyboardEvent, KeyboardEventHandler, MouseEvent, MouseEventHandler } from 'react';
import { isLinkOut, navigateTo, getBaseUrl } from 'utils';
import type { LinkProps, WrappedComponent } from './types';

/**
 * Returns true if the event target is a nested interactive element inside card body/text content.
 * These elements should handle their own interactions independently of the masked link.
 */
function isNestedCardBodyInteractive(target: HTMLElement, currentTarget: EventTarget | null): boolean {
  const interactiveElement = target.closest('a, button');

  if (!interactiveElement || interactiveElement === currentTarget) return false;

  return !!interactiveElement.closest('.card__body, .card__text-content');
}

/**
 * Creates a click handler that navigates to the specified URL when invoked.
 *
 * @param href - The URL to navigate to
 * @param handler
 * @param target - The target attribute (e.g., '_blank' for new tab)
 * @param rel - The rel attribute (e.g., 'noopener noreferrer' for security when using target='_blank')
 * @returns A click event handler function
 */
function createClickHandler(href: string, handler?: MouseEventHandler, target?: string, rel?: string) {
  // If a custom click handler is provided, use it; otherwise, create a default one that performs navigation
  return handler
    ? handler
    : (e: MouseEvent) => {
        // If the click target is a link or button (not the card itself), let it handle its own action
        const clickTarget = e.target as HTMLElement;
        const interactiveElement = clickTarget.closest('a, button');

        if (interactiveElement && interactiveElement !== e.currentTarget) {
          // Let the nested link/button handle the click
          return;
        }

        e.preventDefault();
        navigateTo(href, target, rel);
      };
}

function wrapClickHandler(href: string, handler?: MouseEventHandler, target?: string, rel?: string): MouseEventHandler {
  return (e: MouseEvent) => {
    // If the click target is a nested interactive element inside card body/text content, let it handle its own action
    if (isNestedCardBodyInteractive(e.target as HTMLElement, e.currentTarget)) {
      return;
    }

    if (handler) {
      handler(e);
    }

    if (!e.defaultPrevented) {
      e.preventDefault();
      navigateTo(href, target, rel);
    }
  };
}

function wrapKeyDownHandler(
  href: string,
  handler?: MouseEventHandler | KeyboardEventHandler,
  target?: string,
  rel?: string
): KeyboardEventHandler {
  return (e: KeyboardEvent) => {
    // If the focus is on a nested interactive element inside card body/text content, let it handle its own keyboard action
    if (isNestedCardBodyInteractive(e.target as HTMLElement, e.currentTarget)) {
      return;
    }

    if (handler) {
      const handle = handler as KeyboardEventHandler;
      handle(e);
    }

    if (!e.defaultPrevented && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      navigateTo(href, target, rel);
    }
  };
}

/**
 * Creates a keydown handler that navigates to the specified URL when the Enter or Space key is pressed.
 *
 * @param href - The URL to navigate to
 * @param handler
 * @param target - The target attribute (e.g., '_blank' for new tab)
 * @param rel - The rel attribute (e.g., 'noopener noreferrer' for security when using target='_blank')
 * @returns A keydown event handler function
 */
function createKeyDownHandler(
  href: string,
  handler?: MouseEventHandler | KeyboardEventHandler,
  target?: string,
  rel?: string
) {
  return (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // If the focus is on a nested interactive element inside card body/text content, let it handle its own keyboard action
      if (isNestedCardBodyInteractive(e.target as HTMLElement, e.currentTarget)) {
        return;
      }

      e.preventDefault();

      // If a keyboard-specific handler is provided, call it; otherwise, call the mouse handler and coercively cast
      // to the appropriate type
      if (handler) {
        const handle = handler as KeyboardEventHandler;
        handle(e);
      } else {
        // No custom handler, perform navigation
        navigateTo(href, target, rel);
      }
    }
  };
}

/**
 * Determines the appropriate target attribute for a link based on whether it's an external link or not. If a target is
 * already provided, it uses that. Otherwise, it sets target to '_blank' for external links and '_self' for internal
 * links. This helps ensure that external links open in a new tab by default, while internal links open in the same tab.
 *
 * @param href - The URL of the link
 * @param target - The target attribute, if already specified
 * @returns The appropriate target attribute for the link
 */
function createLinkTarget(href?: string, target?: string) {
  if (!href || target) return target;

  return isLinkOut(href, getBaseUrl()) ? '_blank' : '_self';
}

/**
 * Determines the appropriate rel attribute for a link based on whether it's an external link or not. If a rel is
 * already provided, it uses that. Otherwise, it sets rel to 'noopener noreferrer' for external links to enhance
 * security and privacy when opening links in a new tab. For internal links, it returns undefined, allowing the browser
 * to use its default behavior.
 *
 * @param href - The URL of the link
 * @param rel - The rel attribute, if already specified
 * @returns The appropriate rel attribute for the link
 */
function createLinkRel(target?: string, rel?: string) {
  if (rel) return rel;

  return target === '_blank' ? 'noopener' : undefined;
}

/**
 * HOC that adds link masking functionality to a component. When `masked` is true, it removes the `href` attribute and
 * adds click and keydown handlers to simulate link behavior. It also ensures accessibility by adding appropriate roles
 * and tabIndex if necessary. When `masked` is false, it restores the `href`, `target`, and `rel` attributes and
 * removes any added handlers. This allows for flexible link behavior while maintaining accessibility standards.
 *
 * @param WrappedComponent - The component to wrap with link masking functionality
 * @template Props - The props type of the wrapped component, extending LinkProps
 * @returns A component with link masking functionality
 */
export function withLinkMasking<Props extends LinkProps>(Component: WrappedComponent<Props>) {
  function ComponentWithLinkMasking(props: Props) {
    const {
      href,
      onClick,
      onKeyDown,
      masked = false,
      removeEventHandlersWhenUnmasked = false,
      eventHandlerProcessing = 'wrap',
      dataLink,
      ...rest
    } = props;

    const childProps = { ...rest } as Props;

    const target = createLinkTarget(href, rest.target);
    const rel = createLinkRel(target, rest.rel);

    // When masked, we remove href and add our handlers. We also ensure accessibility by adding role/tabIndex if needed.
    // When unmasked, we restore href/target/rel and remove our handlers if requested. We do not force role/tabIndex
    // when unmasked as the native <a> element handles accessibility.

    if (masked && href) {
      // Remove href and add interactive semantics + handlers
      delete childProps.href;

      childProps.onClick =
        eventHandlerProcessing === 'wrap'
          ? wrapClickHandler(href, onClick, target, rel)
          : createClickHandler(href, onClick, target, rel);
      childProps.onKeyDown =
        eventHandlerProcessing === 'wrap'
          ? wrapKeyDownHandler(href, onKeyDown || onClick, target, rel)
          : createKeyDownHandler(href, onKeyDown || onClick, target, rel);
      // Ensure keyboard accessibility if the child isn't inherently focusable
      if (!childProps.role) childProps.role = 'link';
      if (typeof childProps.tabIndex !== 'number') childProps.tabIndex = 0;

      // data-link is used to display masked link such adjust-link
      if (dataLink) (childProps as Record<string, unknown>)['data-link'] = dataLink;
    } else if (href) {
      // Provide href and remove our handlers for non-masked mode
      childProps.href = href;
      // Ensure target and rel are set appropriately. For example, external links should always open in a new tab with
      // proper rel attributes set. Provided target/rel attributes take precedence.
      childProps.target = target;
      childProps.rel = rel;

      // Inherit provided event handlers unless explicitly told to remove them
      if (!removeEventHandlersWhenUnmasked) {
        if (onClick)
          childProps.onClick =
            eventHandlerProcessing === 'wrap'
              ? wrapClickHandler(href, onClick, target, rel)
              : createClickHandler(href, onClick, target, rel);
        if (onKeyDown) childProps.onKeyDown = onKeyDown;
      }
      // We do not force role/tabIndex when unmasked; native <a> handles accessibility
    }

    return <Component {...childProps} />;
  }

  return ComponentWithLinkMasking;
}
